import { BottomNavigation, BottomNavigationAction, makeStyles } from "@material-ui/core";
import { Home, Apartment, BarChart, Mail, Search } from "@material-ui/icons";
import React from "react";
import { useHistory } from "react-router";
import { useRecoilValue } from "recoil";

import theme from "@/theme";
import { AppEventTokenEnum, isFirstAccess, sendEventToAppMeasurementTool } from "@/utilities/capacitorLogic";
import { NavigationPath } from "@/utilities/enums";
import { UserState } from "@/view_models/atoms";
import { proposalUnreadCount } from "@/view_models/proposalUnreadCounts";

import { CountBadge } from "./CountBadge";

export const BOTTOM_NAV_HEIGHT = 62;
export const BOTTOM_NAV_BOTTOM = 16;
export const BOTTOM_NAV_CONTENT_PADDING = `calc(${BOTTOM_NAV_HEIGHT + BOTTOM_NAV_BOTTOM + 16}px + env(safe-area-inset-bottom, 0px))`;

const useStyles = makeStyles(() => ({
  nav: {
    position: "fixed",
    left: 16,
    right: 16,
    bottom: `calc(${BOTTOM_NAV_BOTTOM}px + env(safe-area-inset-bottom, 0px))`,
    height: BOTTOM_NAV_HEIGHT,
    padding: 4,
    backgroundColor: theme.palette.semantic["surface/card"],
    borderRadius: 9999,
    boxShadow: "0 0 8px rgba(0, 0, 0, 0.08)",
    zIndex: 1299,
  },
  item: {
    minWidth: 0,
    maxWidth: "none",
    height: 54,
    padding: "4px 4px 8px",
    gap: 4,
    color: theme.palette.semantic["icon/default"],
    borderRadius: 9999,
    "&$selected": {
      color: theme.palette.semantic["text/inverse"],
      backgroundColor: theme.palette.semantic["action/primary"],
    },
    "& .MuiSvgIcon-root": { width: 24, height: 24 },
  },
  selected: {},
  label: {
    fontSize: 10,
    fontWeight: 500,
    lineHeight: 1,
    whiteSpace: "nowrap",
    "&$selected": { fontSize: 10 },
  },
  messageIcon: { position: "relative", display: "flex" },
  badge: { position: "absolute", top: -2, left: 15 },
}));

interface NavigationItem {
  path: NavigationPath;
  url: string;
  onClickLink: () => void;
}

interface NavigationBarProps {
  path: NavigationPath;
  onAddButtonClick?: () => void;
}

const NavigationBar: React.FC<NavigationBarProps> = ({ path }) => {
  const history = useHistory();
  const user = useRecoilValue(UserState);
  const classes = useStyles();

  const handleClickEvent = (eventTokenFirst: AppEventTokenEnum, eventTokenSecond: AppEventTokenEnum) => {
    sendEventToAppMeasurementTool(isFirstAccess(user?.created_at) ? eventTokenFirst : eventTokenSecond);
  };

  const navigationItems: NavigationItem[] = [
    {
      path: NavigationPath.HOME,
      url: "/mypage",
      onClickLink: () =>
        handleClickEvent(AppEventTokenEnum.MypageFirstAccess, AppEventTokenEnum.MypageSecondOnwardsAccess),
    },
    {
      path: NavigationPath.AGENT_SEARCH,
      url: "/agent_search",
      onClickLink: () =>
        handleClickEvent(AppEventTokenEnum.AgentSearchFirstAccess, AppEventTokenEnum.AgentSearchSecondOnwardsAccess),
    },
    {
      path: NavigationPath.OFFER,
      url: "/offer",
      onClickLink: () =>
        handleClickEvent(AppEventTokenEnum.OfferFirstAccess, AppEventTokenEnum.OfferSecondOnwardsAccess),
    },
    {
      path: NavigationPath.PROPERTY_MANAGEMENT_OWNED,
      url: "/management/own",
      onClickLink: () => {
        handleClickEvent(
          AppEventTokenEnum.OwnPropertyManagementFirstAccess,
          AppEventTokenEnum.OwnPropertyManagementSecondOnwardsAccess
        );
      },
    },
    {
      path: NavigationPath.PROPERTY_MANAGEMENT_CONSIDERATION,
      url: "/management/consideration",
      onClickLink: () => {
        handleClickEvent(
          AppEventTokenEnum.ConsiderationPropertyManagementFirstAccess,
          AppEventTokenEnum.ConsiderationPropertyManagementSecondOnwardsAccess
        );
      },
    },
  ];

  const handleNavigation = (_, selectedPath: NavigationPath) => {
    const targetItem = navigationItems.find((v) => v.path === selectedPath);

    if (targetItem) {
      targetItem.onClickLink();
      history.push(targetItem.url);
    }
  };

  // オファーアイコンのバッジ制御
  const unreadCount = useRecoilValue(proposalUnreadCount);
  const actionClasses = { root: classes.item, label: classes.label, selected: classes.selected };

  return (
    <>
      <BottomNavigation
        value={path}
        onChange={handleNavigation}
        showLabels
        className={classes.nav}
        aria-label="メインナビゲーション"
      >
        <BottomNavigationAction value={NavigationPath.HOME} classes={actionClasses} label="ホーム" icon={<Home />} />
        <BottomNavigationAction
          value={NavigationPath.AGENT_SEARCH}
          classes={actionClasses}
          id="top-tour-step3"
          label="プランナー"
          icon={<Search />}
        />
        <BottomNavigationAction
          value={NavigationPath.OFFER}
          classes={actionClasses}
          label="メッセージ"
          icon={
            <span className={classes.messageIcon}>
              <Mail />
              <span className={classes.badge}>
                <CountBadge count={unreadCount} />
              </span>
            </span>
          }
        />
        <BottomNavigationAction
          value={NavigationPath.PROPERTY_MANAGEMENT_OWNED}
          classes={actionClasses}
          id="top-tour-step1"
          label="保有物件"
          icon={<Apartment />}
        />
        <BottomNavigationAction
          value={NavigationPath.PROPERTY_MANAGEMENT_CONSIDERATION}
          classes={actionClasses}
          id="top-tour-step2"
          label="検討中物件"
          icon={<BarChart />}
        />
      </BottomNavigation>
    </>
  );
};

export default NavigationBar;
